/**
 * @file src/components/JsonModal/JsonModal.tsx
 * @exports JsonModal
 * @summary Modal that renders arbitrary JSON data as formatted HTML for display. Uses Mantine Modal for WCAG 2.1.1-compliant close-button keyboard handling (Space and Enter both fire the close handler).
 */

import { HeadingH2, colors } from "@cerebruminc/cerebellum";
import { Modal } from "@mantine/core";
import { styled } from "styled-components";
import { transform, isObject, isEmpty } from "lodash";
import { jsonToHtml } from "src/views/UserView/helpers/htmlfy-json";

interface JsonModalType {
	showModal: boolean;
	closeModal: () => void;
	data: any;
	title?: string;
}

const Title = styled(HeadingH2)`
  margin-bottom: 10px;
`;

const StyledJson = styled.div`
  background: ${colors.COOL_GREY_4};
  border-radius: 10px;
  font-family: 'Nunito Sans', 
      system-ui,
      -apple-system,
      'Segoe UI',
      Roboto,
      Helvetica,
      Arial,
      sans-serif,
      'Apple Color Emoji',
      'Segoe UI Emoji';
  padding: 30px;
  
  /* Simplified nesting styles to work with testing.ts */
  h1, h2, h3, h4, h5, h6 {
    margin-bottom: 10px;
    font-weight: bold;
  }
  
  /* Remove redundant margin-left that duplicates what's in testing.ts */
  .json-content {
    /* The margin-left: 10px is already applied in testing.ts */
  }
  
  /* Simplified list styling */
  ul {
    margin: 0;
    padding-left: 20px;
    
    li {
      margin: 5px 0;
    }
  }
`;

const customStyles = {
	container:
		"font-family: 'Nunito Sans', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; line-height: 1.5em;",
	content: "margin-left: 10px;", // Ensure consistent 10px margin for all content
};

export const JsonModal = ({ closeModal, showModal, data, title }: JsonModalType) => {
	const deepClean = (obj: any) => {
		return transform(obj, (result: any, value: any, key: string) => {
			const isEmptyArray = Array.isArray(value) && value.length === 0;
			if (isEmptyArray) return; // Skip empty arrays

			const processedValue = isObject(value) ? deepClean(value) : value;
			if (isObject(processedValue) && !isEmpty(processedValue)) {
				result[key] = processedValue;
			} else if (!isObject(processedValue)) {
				result[key] = processedValue;
			}
		});
	};

	const cleanData = deepClean(data);
	console.log("THis is the clean data", cleanData);
	const html = jsonToHtml(cleanData, {
		useStyles: customStyles,
		formatKeys: true,
		autoHeaders: { includeArrays: true, maxHeaderLevel: 4, minDepth: 0 },
	});
	return (
		<Modal
			onClose={closeModal}
			opened={showModal}
			styles={{
				body: { padding: "46px 50px 63px" },
				content: { "@media (max-width: 556px)": { minWidth: "100vw", height: "100dvh" } },
			}}
			withCloseButton
		>
			{title && <Title>{title}</Title>}
			<StyledJson dangerouslySetInnerHTML={{ __html: html }} />
		</Modal>
	);
};
